import { action, observable, computed } from 'mobx';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import { sortString, sortNumber } from '../utils';
import { NO_DOCUMENTS_MESSAGE, DISALLOWED_FILTER_PARAMS } from '../constants';
import { DocumentSearch } from '../models/configuration/documents/DocumentSearch';
import { DataStore } from '../stores/DataStore';
import { ShareClassDocument } from '../models/entity';
import { DocumentModuleConfig } from '../models/configuration/DocumentModuleConfig';
import {
    intersect,
    union,
} from '@kurtosys/ksys-app-template/dist/utils/collection';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';

export class DocumentModuleStore {
    @observable dataStore: DataStore;
    @observable documents: Array<ShareClassDocument> = [];
    @observable config: DocumentModuleConfig;
    initialised: boolean = false;

    constructor(dataStore) {
        this.dataStore = dataStore;
        this.config = dataStore.config.config;

        if (!this.initialised) {
            this.init();
        }
    }

    init = () => {
        this.initialised = true;
        this.getDocuments();
    };

    @computed
    get filterProperties() {
        const dataStore = this.dataStore;
        return dataStore.config.filtersConfig
            .filter(filter => filter.type === 'CLSS')
            .map(filter => filter.property)
            .concat(Object.keys(dataStore.filterParams))
            .filter(property => !DISALLOWED_FILTER_PARAMS.includes(property));
    }

    @action
    getDocuments = async () => {
        const loadingId = this.dataStore.stores.loading.start('global');
        const promises = [];

        if (this.dataStore.watchListSearchEnabled) {
            promises.push(this.getDocumentsFromSearch(this.watchlistSearches));
        }
        if (this.hasIdentifierSearch) {
            promises.push(
                this.getDocumentsFromSearch(this.identifierDocumentSearches)
            );
        }
        if (this.hasFilterSearch) {
            promises.push(
                this.getDocumentsFromSearch([this.filterDocumentSearch])
            );
        }
        if (promises.length === 0) {
            promises.push(this.getDocumentsFromSearch([this.documentSearch]));
        }

        try {
            const results = await Promise.all(promises);
            if (results.length === 1) {
                this.documents = results[0];
            } else {
                let documents: Array<any> = results.shift();
                for (const result of results) {
                    documents = intersect(
                        documents,
                        result,
                        item => item.documentId
                    );
                }
                this.documents = documents;
            }
        } catch (ex) {
            console.warn(ex);
        }
        this.dataStore.stores.loading.stop('global', loadingId);
    };

    @computed
    get watchlistSearches(): Array<DocumentSearch> {
        const { watchListSearch } = this.dataStore;
        const documentSearches = watchListSearch.map(searchItem => {
            const search = [
                {
                    property: searchItem.property,
                    values: searchItem.values,
                    matchtype: searchItem.matchtype,
                },
                ...this.searchFromConfig,
            ];
            return {
                ...this.searchRequest,
                search,
            };
        });
        return documentSearches;
    }

    @computed
    get hasIdentifierSearch() {
        const { identifierSearch } = this.dataStore;
        return identifierSearch && !isNullOrEmpty(identifierSearch.value);
    }

    @computed
    get identifierDocumentSearches(): Array<DocumentSearch> {
        const { identifierSearch } = this.dataStore;
        if (!identifierSearch || isNullOrEmpty(identifierSearch.value)) {
            return [];
        }

        const matchtype = identifierSearch.fuzzy ? 'LIKE' : 'MATCH';
        const values = [identifierSearch.value];

        const documentSearches = identifierSearch.properties.map(property => {
            const search = [
                {
                    property,
                    values,
                    matchtype,
                },
                ...this.searchFromConfig,
            ];
            return {
                ...this.searchRequest,
                search,
            };
        });
        return documentSearches;
    }

    @computed
    get hasFilterSearch() {
        return this.filterSearchItems.length > 0;
    }

    @computed
    get filterSearchItems() {
        const { filterSearch = [], documentFilterSearch = [] } = this.dataStore;
        const cleanFilterSearch = filterSearch.filter(s =>
            this.filterProperties.includes(s.property)
        );
        const response = [];
        if (cleanFilterSearch && cleanFilterSearch.length > 0) {
            response.push(...cleanFilterSearch);
        }
        response.push(...documentFilterSearch);
        return response;
    }

    @computed
    get filterDocumentSearch(): DocumentSearch {
        const search = this.filterSearchItems.map(searchItem => {
            return {
                property: searchItem.property,
                values: searchItem.values,
                matchType: 'MATCH',
            };
        });

        const result = {
            ...this.searchRequest,
            search: [...search, ...this.searchFromConfig],
        };
        return result;
    }

    @computed
    get documentSearch(): DocumentSearch {
        return {
            ...this.searchRequest,
            search: this.searchFromConfig,
        };
    }

    async getDocumentsFromSearch(documentSearches: Array<any>) {
        let documents: Array<any> = [];
        try {
            const promises = documentSearches.map(
                this.dataStore.services.getDocuments
            );
            const responses = await Promise.all<any>(promises);

            for (const response of responses) {
                documents = union(
                    documents,
                    response.values,
                    item => item.documentId
                );
            }
        } catch (ex) {
            console.warn(ex);
        }
        return documents;
    }

    @computed
    get searchFromConfig() {
        const searchConfig = get(this.config, 'search.config.search', []);
        const segmentationSearchConfig = get(
            this.config,
            `search.segmentationSearch.${this.dataStore.fundlist}`,
            []
        );
        const search = searchConfig
            .concat(segmentationSearchConfig.slice())
            .reduce((accumulator, currentValue) => {
                const existingPropertySearch = accumulator.find(
                    a => a.property === currentValue.property
                );

                if (existingPropertySearch) {
                    existingPropertySearch.values = existingPropertySearch.values.concat(
                        currentValue.values
                    );
                    return accumulator;
                }

                accumulator.push(currentValue);

                return accumulator;
            }, []);
        return search;
    }

    @computed
    get searchRequest() {
        return get(this.config, 'search.config', {});
    }

    // Filters documents based on document properties
    // Loop over document property searches and get the value of any matching document property,
    // then check if that value is included in the filter values
    documentPropertyFilter = document => {
        for (const filter of this.dataStore.documentPropertySearch) {
            const documentProperty = get(document, filter.property);
            if (documentProperty) {
                return filter.values.includes(documentProperty);
            }
        }
    };

    @computed
    get groupedDocuments() {
        const { cultureAliases = {} } = this.config;
        const { documentPropertySearch } = this.dataStore;

        const filteredDocuments =
            documentPropertySearch.length > 0
                ? this.documents.filter(document =>
                      this.documentPropertyFilter(document)
                  )
                : this.documents;

        const groupingField =
            this.grouping.property || `meta.${this.grouping.meta}.value`;

        const groupedDocuments = groupBy(filteredDocuments, groupingField);

        return Object.keys(groupedDocuments)
            .sort(sortString)
            .map(documentGroupName => {
                const documentGroupNameIndex = this.grouping.order.indexOf(
                    documentGroupName
                );
                return {
                    order:
                        documentGroupNameIndex > -1
                            ? documentGroupNameIndex
                            : this.grouping.order.length,
                    documentGroupName,
                    documents: groupedDocuments[documentGroupName].sort(
                        (a, b) =>
                            sortString(
                                get(
                                    cultureAliases,
                                    a.cultureCode,
                                    a.cultureCode
                                ),
                                get(
                                    cultureAliases,
                                    b.cultureCode,
                                    b.cultureCode
                                )
                            )
                    ),
                };
            })
            .sort((a, b) => sortNumber(a.order, b.order));
    }

    @computed
    get grouping() {
        const { grouping = {} } = this.config;
        const defaults = {
            order: [],
            meta: 'document_type',
        };
        return {
            ...defaults,
            ...grouping,
        };
    }

    @computed
    get header() {
        const { header = {} } = this.config;
        const defaults = {
            label: 'Documents',
        };
        return {
            ...defaults,
            ...header,
        };
    }

    @computed
    get headerDisclaimers(): Array<string> {
        const disclaimerTypes = get(this.config, 'disclaimers.header', []);

        return this.dataStore.disclaimerStore.getUnlinkedDisclaimers(
            disclaimerTypes
        );
    }

    @computed
    get footerDisclaimers(): Array<string> {
        const disclaimerTypes = get(this.config, 'disclaimers.footer', []);

        return this.dataStore.disclaimerStore.getUnlinkedDisclaimers(
            disclaimerTypes
        );
    }

    @computed
    get noDataDisclaimers(): Array<string> {
        const disclaimerTypes = get(this.config, 'disclaimers.noData', []);
        return disclaimerTypes.length > 0
            ? this.dataStore.disclaimerStore.getUnlinkedDisclaimers(
                  disclaimerTypes
              )
            : [NO_DOCUMENTS_MESSAGE];
    }
}



// WEBPACK FOOTER //
// ./src/stores/DocumentModuleStore.ts